<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
});

const POLL_INTERVAL = 5000;
const MAX_PAIRING_POLLS = 60;

const { t } = useI18n();

const state = ref(null);
const qrCode = ref('');
const isLoading = ref(true);
const isPairing = ref(false);
const hasTimedOut = ref(false);
let pollTimer = null;
let pairingPolls = 0;

const isConnected = computed(() => state.value === 'open');

const statusLabel = computed(() => {
  if (isConnected.value) return t('INBOX_MGMT.EVOLUTION.STATUS.CONNECTED');
  if (state.value) return t('INBOX_MGMT.EVOLUTION.STATUS.DISCONNECTED');
  return t('INBOX_MGMT.EVOLUTION.STATUS.UNREACHABLE');
});

const statusDotClass = computed(() => {
  if (isConnected.value) return 'bg-n-teal-9';
  if (state.value) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
});

const stopPolling = () => {
  clearTimeout(pollTimer);
  pollTimer = null;
};

const fetchState = async () => {
  try {
    const { data } = await InboxesAPI.getEvolutionConnection(props.inboxId);
    state.value = data.state;
  } catch {
    state.value = null;
  } finally {
    isLoading.value = false;
  }
};

const finishPairing = () => {
  stopPolling();
  isPairing.value = false;
  qrCode.value = '';
};

const pollPairing = async () => {
  pairingPolls += 1;
  try {
    const { data } = await InboxesAPI.connectEvolution(props.inboxId);
    state.value = data.state;
    if (data.qr_code) qrCode.value = `data:image/png;base64,${data.qr_code}`;
  } catch {
    state.value = null;
  }

  if (isConnected.value) {
    finishPairing();
    return;
  }
  if (pairingPolls >= MAX_PAIRING_POLLS) {
    finishPairing();
    hasTimedOut.value = true;
    return;
  }
  pollTimer = setTimeout(pollPairing, POLL_INTERVAL);
};

const startPairing = () => {
  stopPolling();
  pairingPolls = 0;
  hasTimedOut.value = false;
  qrCode.value = '';
  isPairing.value = true;
  pollPairing();
};

onMounted(fetchState);
onBeforeUnmount(stopPolling);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap gap-4 justify-between items-center">
      <div class="flex gap-2 items-center text-sm text-n-slate-12">
        <span class="rounded-full size-2.5" :class="statusDotClass" />
        <span v-if="isLoading">{{
          t('INBOX_MGMT.EVOLUTION.STATUS.LOADING')
        }}</span>
        <span v-else>{{ statusLabel }}</span>
      </div>
      <NextButton
        v-if="!isLoading && !isConnected && !isPairing"
        :label="t('INBOX_MGMT.EVOLUTION.CONNECT_BUTTON')"
        icon="i-ri-qr-code-line"
        @click="startPairing"
      />
      <NextButton
        v-if="isPairing"
        :label="t('INBOX_MGMT.EVOLUTION.CANCEL_BUTTON')"
        slate
        faded
        @click="finishPairing"
      />
    </div>

    <div v-if="isPairing" class="flex flex-col gap-3 items-center">
      <p class="text-sm text-center text-n-slate-11">
        {{ t('INBOX_MGMT.EVOLUTION.QR_INSTRUCTION') }}
      </p>
      <img
        v-if="qrCode"
        :src="qrCode"
        :alt="t('INBOX_MGMT.EVOLUTION.QR_ALT')"
        class="bg-white rounded-lg size-64"
      />
      <p v-else class="text-sm text-n-slate-10">
        {{ t('INBOX_MGMT.EVOLUTION.QR_LOADING') }}
      </p>
    </div>

    <p v-if="hasTimedOut" class="text-sm text-n-amber-11">
      {{ t('INBOX_MGMT.EVOLUTION.QR_TIMEOUT') }}
    </p>
  </div>
</template>
